import type { ResolvedComponent } from '@inertiajs/react';
import { RippleGround } from '@/components/RippleGround';
import { ServiceList } from '@/components/home/ServiceList';
import { WhyChooseUs } from '@/components/home/WhyChooseUs';
import { Button } from '@/components/ui/Button';
import { Eyebrow } from '@/components/ui/Eyebrow';
import { Quote } from '@/components/ui/Quote';
import { RevealOnScroll } from '@/components/ui/RevealOnScroll';
import { RidgeDivider } from '@/components/ui/RidgeDivider';
import { SectionLabel } from '@/components/ui/SectionLabel';
import { trackUmamiEvent } from '@/lib/analytics';

const wrap = 'mx-auto max-w-[1120px] px-5 md:px-10';
// Home swaps the flat hairline for the ridge-arc divider between its major
// sections (see RidgeDivider) — restrained to this one page, so it reads as
// a rare identity accent rather than a repeated motif across the whole site.
const sectionClassName = 'py-[52px] md:py-20';
const ctasClassName = 'flex flex-col gap-3 min-[520px]:flex-row min-[520px]:flex-wrap';

const Home: ResolvedComponent = () => (
  <>
    {/* Hero — token radial-gradient wash + the ripple ground (ambient
        fingerprint/water texture), matching the mockup's `.hero` (no raw hex). */}
    <section className="relative overflow-hidden bg-[radial-gradient(130%_80%_at_88%_-5%,color-mix(in_srgb,var(--color-accent)_16%,transparent),transparent_62%),radial-gradient(95%_70%_at_-8%_108%,color-mix(in_srgb,var(--color-primary)_20%,transparent),transparent_58%)] pt-[52px] pb-10 md:pt-[84px] md:pb-[68px]">
      <RippleGround />
      <div className={`${wrap} relative`}>
        <Eyebrow className="mb-[18px]" spark>
          Home & commercial cleaning
        </Eyebrow>
        <h1 className="t-hero [&_em]:text-primary-deep [&_em]:italic">
          A home that feels <em>cared for</em>, not just cleaned.
        </h1>
        <p className="text-ink-soft mt-[22px] mb-[30px] max-w-[40ch] text-[clamp(16px,4.4vw,19px)]">
          Thoughtful, reliable cleaning tailored to your space and your schedule — so you always
          come back to calm.
        </p>
        <div className={ctasClassName}>
          <Button
            variant="solid"
            as="link"
            href="/contact"
            onClick={() => trackUmamiEvent('cta_request_visit', { location: 'hero' })}
          >
            Request a visit
          </Button>
          <Button
            variant="ghost"
            as="link"
            href="/services"
            onClick={() => trackUmamiEvent('cta_see_services')}
          >
            See our services
          </Button>
        </div>
        <div className="border-line mt-10 grid grid-cols-1 gap-4 border-t pt-[22px] min-[600px]:grid-cols-3 min-[600px]:gap-7">
          <div>
            <strong className="font-display mb-0.5 block text-[17px] font-normal">
              Regular & deep
            </strong>
            <span className="text-ink-soft text-[13px]">home cleaning</span>
          </div>
          <div>
            <strong className="font-display mb-0.5 block text-[17px] font-normal">Offices</strong>
            <span className="text-ink-soft text-[13px]">& commercial spaces</span>
          </div>
          <div>
            <strong className="font-display mb-0.5 block text-[17px] font-normal">
              Move-in / out
            </strong>
            <span className="text-ink-soft text-[13px]">fresh starts</span>
          </div>
        </div>
      </div>
    </section>

    <div className={wrap}>
      <RidgeDivider />
    </div>

    {/* Why choose us */}
    <RevealOnScroll variant="wipe">
      <section className={sectionClassName}>
        <div className={wrap}>
          <h2 className="t-h2-sec">Why choose us?</h2>
          <WhyChooseUs />
        </div>
      </section>
    </RevealOnScroll>

    <div className={wrap}>
      <RidgeDivider />
    </div>

    {/* Services */}
    <RevealOnScroll variant="wipe">
      <section className={sectionClassName}>
        <div className={wrap}>
          <SectionLabel n="04" title="Services" />
          <ServiceList />
        </div>
      </section>
    </RevealOnScroll>

    <div className={wrap}>
      <RidgeDivider />
    </div>

    {/* Testimonial */}
    <RevealOnScroll>
      <section className={sectionClassName}>
        <div className={wrap}>
          <SectionLabel title="What clients say" />
          <Quote size="lg" by="Sarah M." role="Regular client since 2021">
            “I've never seen my home sparkle like this. Friendly, on time, and incredibly thorough.”
          </Quote>
        </div>
      </section>
    </RevealOnScroll>

    <div className={wrap}>
      <RidgeDivider />
    </div>

    {/* Closing CTA */}
    <RevealOnScroll>
      <section className={sectionClassName}>
        <div className={wrap}>
          <Eyebrow>Get started</Eyebrow>
          <h2 className="t-h2 [&_em]:text-primary-deep mt-4 mb-6 [&_em]:italic">
            Ready for a home that feels <em>cared for</em>?
          </h2>
          <div className={ctasClassName}>
            <Button
              variant="solid"
              as="link"
              href="/contact"
              onClick={() => trackUmamiEvent('cta_request_visit', { location: 'home' })}
            >
              Request a visit
            </Button>
            <Button variant="ghost" as="link" href="/contact">
              hello@puretouchbydaniela.com
            </Button>
          </div>
        </div>
      </section>
    </RevealOnScroll>
  </>
);

export default Home;
